import {
  Accordion,
  AccordionPanel,
  AccordionItem,
  AccordionButton,
  Box,
  Flex,
  Select,
  Text,
  Textarea,
  Input,
  Tooltip,
  AccordionIcon,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Button as ChakraButton } from "@chakra-ui/react";
import {
  List,
  ListItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
  Icon,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import React, { useState, useEffect, useMemo, useRef } from "react";
import Button from "../../../components/ui/button";
import colors from "../../../utils/colors";
import {
  useSavePastMedicalHistory,
  useSaveFamilyHistory,
  useSaveSocialHistory,
  useGetDrugClasses,
  useGetDrugsByClass,
  useGetDosagesByDrugNameAndRoute,
  useGetMedicationFrequency,
  useGetMaleMedicationRoutes,
  useGetFemaleMedicationRoutes,
  useSaveMedicationHistory,
  useSavePhysicalFindingsNote,
  useSearchDrugClasses,
  useSearchDrugs,
} from "../../../apis/patients";

interface ViewHistoryCardProps {
  type: string;
  appointmentId: string;
  patientGender?: "male" | "female";
  patientId: string | null;
}

const ViewHistoryCard: React.FC<ViewHistoryCardProps> = ({
  type,
  appointmentId,
  patientGender,
  patientId,
}) => {
  const [selectedDrugClass, setSelectedDrugClass] = useState("");
  const [selectedDrugName, setSelectedDrugName] = useState("");
  const [selectedRoute, setSelectedRoute] = useState("");

  const { data: drugClasses, isLoading: loadingDrugClasses } =
    useGetDrugClasses();

  const { data: drugNames, isLoading: loadingDrugNames } =
    useGetDrugsByClass(selectedDrugClass);
  const { data: dosages, isLoading: loadingDosages } =
    useGetDosagesByDrugNameAndRoute(selectedDrugName, selectedRoute);

  const isFemale = patientGender?.toLowerCase() === "female";

  const { data: maleRoutes, isLoading: loadingMaleRoutes } =
    useGetMaleMedicationRoutes();
  const { data: femaleRoutes, isLoading: loadingFemaleRoutes } =
    useGetFemaleMedicationRoutes();

  const routes = isFemale ? femaleRoutes : maleRoutes;
  const loadingRoutes = isFemale ? loadingFemaleRoutes : loadingMaleRoutes;
  const { data: frequencies, isLoading: loadingFrequencies } =
    useGetMedicationFrequency();

  const [formState, setFormState] = useState({
    pastMedicalHistory: { category: "", note: "" },
    familyHistory: { category: "", familyMemberAffected: "", note: "" },
    socialHistory: { habits: "", note: "" },
    medicationHistory: {
      name: "",
      route: "",
      dosage: "",
      frequency: "",
      note: "",
    },
    physicalFindings: { note: "", title: "" },
  });
  const {
    mutate: savePastMedicalHistory,
    isPending: isPastMedicalHistoryPending,
  } = useSavePastMedicalHistory();
  const { mutate: saveFamilyHistory, isPending: isFamilyHistoryPending } =
    useSaveFamilyHistory();
  const { mutate: saveSocialHistory, isPending: isSocialHistoryPending } =
    useSaveSocialHistory();
  const {
    mutate: saveMedicationHistory,
    isPending: isMedicationHistoryPending,
  } = useSaveMedicationHistory();
  const { mutate: savePhysicalFindingsNote, isPending: isSavingNote } =
    useSavePhysicalFindingsNote();

  const [medClassSearchTerm, setMedClassSearchTerm] = useState("");
  const [drugNameSearchTerm, setDrugNameSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [filteredDrugNames, setFilteredDrugNames] = useState<
    { name: string; notes?: string }[]
  >([]);

  const medClassSelectRef = useRef<HTMLSelectElement>(null);
  const drugNamesPerPage = 5;

  const handleResetDrugClass = () => {
    setSelectedDrugClass("");
    setMedClassSearchTerm("");
    setSelectedDrugName("");
    setSelectedRoute("");
    setDrugNameSearchTerm("");
    setFilteredDrugNames([]);
    setFormState((prev) => ({
      ...prev,
      medicationHistory: {
        name: "",
        route: "",
        dosage: "",
        frequency: "",
        note: "",
      },
    }));
  };

  useEffect(() => {
    const drugNamesArray = Array.isArray(drugNames) ? drugNames : [];

    const filtered = drugNamesArray.filter(
      (drug: { name: string; notes?: string }) =>
        drug.name.toLowerCase().includes(drugNameSearchTerm.toLowerCase()),
    );

    setFilteredDrugNames(filtered);
    setCurrentPage(1);
  }, [drugNames, drugNameSearchTerm]);

  const totalPages = Math.ceil(filteredDrugNames.length / drugNamesPerPage);

  const displayedDrugNames = filteredDrugNames
    .slice((currentPage - 1) * drugNamesPerPage, currentPage * drugNamesPerPage)
    .map((drug) =>
      typeof drug === "string" ? { name: drug, notes: "" } : drug,
    );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredDrugClasses = useMemo(() => {
    if (isDropdownOpen && !medClassSearchTerm) {
      return drugClasses;
    }
    if (isDropdownOpen && medClassSearchTerm) {
      const lowerCaseSearchTerm = medClassSearchTerm.toLowerCase();
      return drugClasses.filter((drugClass) =>
        drugClass.toLowerCase().includes(lowerCaseSearchTerm),
      );
    }
    return [];
  }, [medClassSearchTerm, drugClasses, isDropdownOpen]);

  const handleInputFocus = () => {
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (
        containerRef.current &&
        !containerRef.current.contains(document.activeElement)
      ) {
        setIsDropdownOpen(false);
        if (!selectedDrugClass && medClassSearchTerm === "") {
          setMedClassSearchTerm("");
        }
      }
    }, 100);
  };

  const handleIconClick = () => {
    setIsDropdownOpen((prev) => !prev);
    if (!isDropdownOpen && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSelectClass = (drugClass: string) => {
    setSelectedDrugClass(drugClass);
    setMedClassSearchTerm(drugClass);
    setIsDropdownOpen(false);

    setSelectedDrugName("");
    setSelectedRoute("");
    // setFormState(prev => ({...}))
    setDrugNameSearchTerm("");
  };

  const handleMedicalHistoryInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newSearchTerm = e.target.value;
    setMedClassSearchTerm(newSearchTerm);

    if (selectedDrugClass && newSearchTerm !== selectedDrugClass) {
      setSelectedDrugClass("");
    }
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const displayValue = isDropdownOpen
    ? medClassSearchTerm
    : selectedDrugClass || medClassSearchTerm;

  const handleInputChange = (
    field:
      | "category"
      | "note"
      | "familyMemberAffected"
      | "habits"
      | "name"
      | "route"
      | "frequency"
      | "dosage"
      | "title",
    value: string,
  ) => {
    if (type === "Past Medical History") {
      setFormState((prev) => ({
        ...prev,
        pastMedicalHistory: {
          ...prev.pastMedicalHistory,
          [field]: value,
        },
      }));
    } else if (type === "Family History") {
      setFormState((prev) => ({
        ...prev,
        familyHistory: {
          ...prev.familyHistory,
          [field]: value,
        },
      }));
    } else if (type === "Social History") {
      setFormState((prev) => ({
        ...prev,
        socialHistory: {
          ...prev.socialHistory,
          [field]: value,
        },
      }));
    } else if (type === "Medication History") {
      setFormState((prev) => ({
        ...prev,
        medicationHistory: {
          ...prev.medicationHistory,
          [field]: value,
        },
      }));
    } else if (type === "Physical Findings") {
      setFormState((prev) => ({
        ...prev,
        physicalFindings: {
          ...prev.physicalFindings,
          [field]: value,
        },
      }));
    }
  };

  const handlePhyscicalFindingsSave = () => {
    const { note, title } = formState.physicalFindings;

    if (!title && !note) {
      console.error("Validation Error: Title or Note is required.");
      return;
    }

    savePhysicalFindingsNote({
      appointmentIdString: appointmentId,
      patientId,
      content: note || "",
      title: title || "",
    });
  };

  const handleSave = () => {
    const { category, note } = formState.pastMedicalHistory;

    if (!category && !note) {
      console.error("Validation Error: Category or Note is required.");
      return;
    }

    savePastMedicalHistory({
      appointmentIdString: appointmentId,
      pastMedicalHistories: [{ category: category || "", notes: note || "" }],
    });
  };

  const handleFamilyHistorySave = () => {
    const { category, familyMemberAffected, note } = formState.familyHistory;
    const hasPickableInput = category || familyMemberAffected;
    const hasNoteInput = note;

    if (!hasPickableInput && !hasNoteInput) {
      console.error(
        "Validation Error: Category, Family Member, or Note is required.",
      );
      return;
    }

    if (hasPickableInput && (!category || !familyMemberAffected)) {
      console.error(
        "Validation Error: Both Category and Family Member Affected are required if either is selected.",
      );
      return;
    }

    saveFamilyHistory({
      appointmentIdString: appointmentId,
      familyHistories: [
        {
          category: category || "",
          familyMemberAffected: familyMemberAffected || "",
          notes: note || "",
        },
      ],
    });
  };

  const handleSocialHistorySave = () => {
    const { habits, note } = formState.socialHistory;

    if (!habits && !note) {
      console.error("Validation Error: Habit or Note is required.");
      return;
    }

    saveSocialHistory({
      appointmentIdString: appointmentId,
      socialHistories: [
        {
          habits: habits || "",
          notes: note || "",
        },
      ],
    });
  };

  const generateRandomObjectId = () => {
    return (
      Math.random().toString(16).substr(2, 8) +
      Math.random().toString(16).substr(2, 8) +
      Math.random().toString(16).substr(2, 8)
    );
  };
  const handleMedicationHistorySave = () => {
    const { name, route, dosage, frequency, note } =
      formState.medicationHistory;
    const hasPickableInput = name || route || dosage || frequency;
    const hasNoteInput = note;

    if (!hasPickableInput && !hasNoteInput) {
      console.error(
        "Validation Error: Medication details or Note is required.",
      );
      return;
    }

    if (hasPickableInput && (!name || !route || !dosage || !frequency)) {
      console.error(
        "Validation Error: Name, Route, Dosage, and Frequency are required if any medication detail is entered.",
      );
      return;
    }

    const generatedId = generateRandomObjectId();
    saveMedicationHistory({
      appointmentId: appointmentId,
      patientId: patientId,
      medications: [
        {
          medicationId: generatedId,
          name: name || "",
          route: route || "",
          dosage: dosage || "",
          frequency: frequency || "",
          notes: note || "",
        },
      ],
    });
  };

  const pmhAllEmpty =
    !formState.pastMedicalHistory.category &&
    !formState.pastMedicalHistory.note;

  const fh = formState.familyHistory;
  const fhPickablesStarted = fh.category || fh.familyMemberAffected;
  const fhNoteEntered = fh.note;
  const fhAllEmpty = !fhPickablesStarted && !fhNoteEntered;
  const fhPickablesIncomplete =
    fhPickablesStarted && (!fh.category || !fh.familyMemberAffected);
  const isFamilyHistoryInvalid = fhAllEmpty || fhPickablesIncomplete;

  const shAllEmpty =
    !formState.socialHistory.habits && !formState.socialHistory.note;

  const mh = formState.medicationHistory;
  const mhPickablesStarted = mh.name || mh.route || mh.dosage || mh.frequency;
  const mhNoteEntered = mh.note;
  const mhAllEmpty = !mhPickablesStarted && !mhNoteEntered;
  const mhPickablesIncomplete =
    mhPickablesStarted &&
    (!mh.name || !mh.route || !mh.dosage || !mh.frequency);
  const isMedicationHistoryInvalid = mhAllEmpty || mhPickablesIncomplete;

  const [globalSearch, setGlobalSearch] = useState("");

  const { data: classSearchResults, isLoading: loadingClassSearch } =
    useSearchDrugClasses(globalSearch);

  const { data: drugSearchResults, isLoading: loadingDrugSearch } =
    useSearchDrugs(globalSearch);

  const pfAllEmpty =
    !formState.physicalFindings.title && !formState.physicalFindings.note;

  return (
    <Flex
      backgroundColor={"white"}
      borderRadius={"8px"}
      gap={"20px"}
      boxShadow={`1px 4px 15px 0px #3030301F`}
      width={"100%"}
      direction={"column"}
      padding={"40px"}
    >
      {type === "Social History" ? (
        <>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Habits
            </Text>
            <Select
              placeholder="Select habit"
              focusBorderColor={colors.primary}
              value={formState.socialHistory.habits}
              onChange={(e) => handleInputChange("habits", e.target.value)}
            >
              <option value="Smoking">Smoking</option>
              <option value="Alcohol">Alcohol consumption</option>
              <option value="Stimulants">
                Use of Stimulants/Coffee/Psychoactive Substances
              </option>
              <option value="Others">Others</option>
            </Select>
          </Box>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Notes
            </Text>
            <Textarea
              placeholder="Enter note"
              focusBorderColor={colors.primary}
              value={formState.socialHistory.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
            />
          </Box>
          <Flex style={{ justifyContent: "flex-end" }}>
            <Button
              theme="primary"
              text="Submit"
              className="rounded-md mt-2 w-[150px]"
              onClick={handleSocialHistorySave}
              disabled={Boolean(isSocialHistoryPending || shAllEmpty)}
            />
          </Flex>
        </>
      ) : type === "Medication History" ? (
        <>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              mb="12px"
              color={colors.dark}
            >
              Medication class
            </Text>

            <Box mb={4}>
              <Text
                fontFamily="lato"
                fontSize="14px"
                fontWeight={600}
                mb="12px"
                color={colors.dark}
              >
                Search drug or class
              </Text>

              <Popover
                isOpen={(globalSearch || "").trim().length >= 2}
                placement="bottom-start"
                autoFocus={false}
              >
                <PopoverTrigger>
                  <Input
                    placeholder="Type at least 2 letters (e.g., amox, penic...)"
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    focusBorderColor={colors.primary}
                    autoComplete="off"
                  />
                </PopoverTrigger>

                <PopoverContent p={3} w="100%" maxW="520px">
                  {loadingClassSearch || loadingDrugSearch ? (
                    <Flex align="center" gap={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm">Searching...</Text>
                    </Flex>
                  ) : (
                    <>
                      {/* Matching classes */}
                      <Box mb={3}>
                        <Text fontSize="sm" fontWeight={700} mb={2}>
                          Classes
                        </Text>
                        {!classSearchResults ||
                        classSearchResults.length === 0 ? (
                          <Text fontSize="sm" color="gray.500">
                            No matching classes
                          </Text>
                        ) : (
                          <Wrap spacing="8px">
                            {classSearchResults.map((cls) => (
                              <WrapItem key={cls}>
                                <ChakraButton
                                  size="sm"
                                  variant="outline"
                                  colorScheme="blue"
                                  onClick={() => {
                                    handleSelectClass(cls);
                                    setGlobalSearch("");
                                  }}
                                >
                                  {cls}
                                </ChakraButton>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}
                      </Box>

                      {/* Matching drugs */}
                      <Box>
                        <Text fontSize="sm" fontWeight={700} mb={2}>
                          Drugs
                        </Text>
                        {!drugSearchResults ||
                        drugSearchResults.length === 0 ? (
                          <Text fontSize="sm" color="gray.500">
                            No matching drugs
                          </Text>
                        ) : (
                          <List spacing={2}>
                            {drugSearchResults.map((d) => (
                              <ListItem key={`${d.drugClass}::${d.name}`}>
                                <ChakraButton
                                  w="100%"
                                  variant="ghost"
                                  justifyContent="space-between"
                                  onClick={() => {
                                    // auto-select class + drug
                                    setSelectedDrugClass(d.drugClass);
                                    setMedClassSearchTerm(d.drugClass);
                                    setIsDropdownOpen(false);

                                    setSelectedDrugName(d.name);
                                    setSelectedRoute("");

                                    setFormState((prev) => ({
                                      ...prev,
                                      medicationHistory: {
                                        ...prev.medicationHistory,
                                        name: d.name,
                                        route: "",
                                        dosage: "",
                                        frequency: "",
                                      },
                                    }));

                                    setDrugNameSearchTerm("");
                                    setGlobalSearch("");
                                  }}
                                >
                                  <Box textAlign="left">
                                    <Text fontSize="sm" fontWeight={700}>
                                      {d.name}
                                    </Text>
                                    <Text fontSize="xs" color="gray.600">
                                      {d.drugClass}
                                    </Text>
                                  </Box>
                                  <Text fontSize="xs" color="gray.500">
                                    Select
                                  </Text>
                                </ChakraButton>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    </>
                  )}
                </PopoverContent>
              </Popover>
            </Box>

            <Box position="relative" ref={containerRef}>
              {" "}
              {/* Ref for the outer container */}
              <InputGroup>
                <Input
                  ref={inputRef} // Attach ref
                  placeholder={
                    loadingDrugClasses
                      ? "Loading..."
                      : selectedDrugClass || "Select medication class"
                  } // Show selected or default placeholder
                  value={displayValue} // Bind to the display value
                  onChange={handleMedicalHistoryInputChange} // Handle typing
                  onFocus={handleInputFocus} // Handle input focus
                  onBlur={handleInputBlur} // Handle input blur
                  pr="4.5rem" // Add padding on the right for the icon button
                  // focusBorderColor={colors.primary} // Use theme colors
                  isDisabled={loadingDrugClasses}
                  autoComplete="off" // Prevent browser autocomplete interfering
                />
                <InputRightElement width="4.5rem">
                  {loadingDrugClasses ? (
                    <Spinner size="sm" /> // Show spinner if loading
                  ) : (
                    <IconButton
                      aria-label={
                        isDropdownOpen ? "Close dropdown" : "Open dropdown"
                      }
                      icon={
                        isDropdownOpen ? <ChevronUpIcon /> : <ChevronDownIcon />
                      }
                      onClick={handleIconClick}
                      variant="ghost" // Style to look like part of the input
                      size="sm"
                      onMouseDown={(e) => e.preventDefault()} // Prevent immediate blur
                      isDisabled={loadingDrugClasses}
                    />
                  )}
                </InputRightElement>
              </InputGroup>
              {/* The custom dropdown list - Render only if open AND not loading */}
              {isDropdownOpen &&
                !loadingDrugClasses &&
                (filteredDrugClasses.length > 0 ||
                  (medClassSearchTerm.length > 0 &&
                    filteredDrugClasses.length === 0)) && (
                  <Box
                    position="absolute"
                    top="100%"
                    left="0"
                    right="0"
                    zIndex={1}
                    borderWidth="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    mt={1}
                    bg="white"
                    boxShadow="md"
                    maxHeight="200px"
                    overflowY="auto"
                  >
                    {/* Reset button outside the loop */}
                    <Box p={2} textAlign="center">
                      <ChakraButton
                        onClick={handleResetDrugClass}
                        colorScheme="gray"
                        size="sm"
                        variant="outline"
                      >
                        Reset Drug Class to Default
                      </ChakraButton>
                    </Box>

                    {filteredDrugClasses.length === 0 && medClassSearchTerm ? (
                      <Box p={2} color="gray.500">
                        No classes found
                      </Box>
                    ) : (
                      <Wrap spacing="8px" py={2} px={2}>
                        {filteredDrugClasses.map((drugClass: string) => (
                          <WrapItem key={drugClass}>
                            <Tooltip label={drugClass} hasArrow placement="top">
                              <ChakraButton
                                size="sm"
                                px={3}
                                py={2}
                                fontSize={{ base: "12px", md: "14px" }}
                                minW="120px"
                                maxW="160px"
                                h="auto"
                                whiteSpace="normal"
                                wordBreak="break-word"
                                overflowWrap="break-word"
                                textAlign="left"
                                display="inline-block"
                                variant={
                                  selectedDrugClass === drugClass
                                    ? "solid"
                                    : "outline"
                                }
                                colorScheme="blue"
                                onClick={() => handleSelectClass(drugClass)}
                              >
                                {drugClass}
                              </ChakraButton>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                  </Box>
                )}
              {/* Optional: Display a loading state inside the dropdown area */}
              {isDropdownOpen && loadingDrugClasses && (
                <Box
                  position="absolute"
                  top="100%"
                  left="0"
                  right="0"
                  zIndex={1}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  mt={1}
                  bg="white"
                  boxShadow="md"
                  p={2}
                  textAlign="center"
                  color="gray.500"
                >
                  Loading...
                </Box>
              )}
            </Box>
          </Box>
          {/* Drug Names */}
          <Accordion
            allowToggle
            defaultIndex={selectedDrugClass ? [0] : []}
            key={selectedDrugClass}
          >
            {" "}
            {/* Added allowToggle, defaultIndex and key */}
            <AccordionItem border="none">
              {" "}
              {/* Removed border */}
              <h2>
                {" "}
                {/* Added h2 for semantics */}
                <AccordionButton
                  _expanded={{
                    bg: colors.primary + "10",
                    color: colors.primary,
                  }}
                >
                  {" "}
                  {/* Style when expanded */}
                  <Box
                    flex="1"
                    textAlign="left"
                    fontWeight={600}
                    color={colors.dark}
                  >
                    <Box as="span" fontWeight={600}>
                      Name:
                    </Box>{" "}
                    <Box as="span" fontWeight={400}>
                      {formState.medicationHistory.name || "None selected"}{" "}
                      {/* Use formState value */}
                    </Box>
                  </Box>
                  <AccordionIcon /> {/* Added Icon */}
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <Input
                  placeholder="Search drug names..."
                  value={drugNameSearchTerm}
                  onChange={(e) => setDrugNameSearchTerm(e.target.value)}
                  mb={4}
                  focusBorderColor={colors.primary}
                  isDisabled={!selectedDrugClass || loadingDrugNames} // Keep disabled state
                />
                {loadingDrugNames && <Text>Loading drug names...</Text>}{" "}
                {/* Loading indicator */}
                {!loadingDrugNames &&
                  filteredDrugNames.length === 0 &&
                  selectedDrugClass && (
                    <Text>
                      No drug names found for this class or search term.
                    </Text>
                  )}{" "}
                {/* No results message */}
                {!loadingDrugNames && filteredDrugNames.length > 0 && (
                  <>
                    <Box maxW="100%" overflowX="hidden" mt={4}>
                      <Wrap spacing="8px">
                        {displayedDrugNames.map(({ name, notes }) => (
                          <WrapItem>
                            <Tooltip
                              key={name}
                              label={notes || "No notes available"}
                              hasArrow
                              placement="top"
                            >
                              <Box
                                px={3}
                                py={2}
                                fontSize={{ base: "12px", md: "14px" }}
                                minW="120px"
                                maxW="160px"
                                bg="blue.50"
                                border="1px solid"
                                borderColor="blue.200"
                                borderRadius="md"
                                whiteSpace="normal"
                                wordBreak="break-word"
                                overflowWrap="break-word"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                textAlign="center"
                                cursor="pointer"
                                onClick={() => {
                                  setSelectedDrugName(name); // Keep this to fetch routes/dosages
                                  // setSelectedDrugId(name); // Not needed based on new UI
                                  setSelectedRoute(""); // Reset route
                                  handleInputChange("name", name); // Update formik state
                                  // Reset subsequent fields in formState
                                  setFormState((prev) => ({
                                    ...prev,
                                    medicationHistory: {
                                      ...prev.medicationHistory,
                                      name: name, // Set name
                                      route: "",
                                      dosage: "",
                                      frequency: "",
                                    },
                                  }));
                                }}
                              >
                                {name}
                              </Box>
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Box>

                    {totalPages > 1 && ( // Only show pagination if needed
                      <Flex justifyContent="space-between" alignItems="center">
                        <ChakraButton
                          size="sm" // Smaller buttons
                          onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={currentPage === 1}
                        >
                          Prev
                        </ChakraButton>
                        <Text fontSize="sm" alignSelf="center">
                          {" "}
                          {/* Smaller text */}
                          Page {currentPage} of {totalPages}
                        </Text>
                        <ChakraButton
                          size="sm" // Smaller buttons
                          onClick={() =>
                            setCurrentPage((prev) =>
                              Math.min(prev + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </ChakraButton>
                      </Flex>
                    )}
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          {/* Route */}
          <Box mb={4}>
            {" "}
            {/* Added mb={4} */}
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              mb="12px"
              color={colors.dark}
            >
              Route
            </Text>
            <Select
              placeholder={loadingRoutes ? "Loading..." : "Select route"}
              focusBorderColor={colors.primary}
              value={formState.medicationHistory.route} // Bind value
              isDisabled={!formState.medicationHistory.name || loadingRoutes} // Use formState value for disabled check
              onChange={(e) => {
                const route = e.target.value;
                setSelectedRoute(route); // Keep this to fetch dosages
                handleInputChange("route", route); // Update formik state
                // Reset subsequent fields
                setFormState((prev) => ({
                  ...prev,
                  medicationHistory: {
                    ...prev.medicationHistory,
                    dosage: "",
                    frequency: "",
                  },
                }));
              }}
            >
              {routes?.map((route) => (
                <option key={route} value={route} style={{ color: "black" }}>
                  {" "}
                  {/* Added style */}
                  {route}
                </option>
              ))}
            </Select>
          </Box>
          {/* Dosage */}
          <Box mb={4}>
            {" "}
            {/* Added mb={4} */}
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              mb="12px"
              color={colors.dark}
            >
              Dosage
            </Text>
            <Select
              placeholder={loadingDosages ? "Loading..." : "Select dosage"}
              focusBorderColor={colors.primary}
              value={formState.medicationHistory.dosage} // Bind value
              isDisabled={!formState.medicationHistory.route || loadingDosages} // Use formState value for disabled check
              onChange={(e) => {
                handleInputChange("dosage", e.target.value);
                // Reset frequency if needed
                setFormState((prev) => ({
                  ...prev,
                  medicationHistory: {
                    ...prev.medicationHistory,
                    frequency: "",
                  },
                }));
              }}
            >
              {dosages?.map((dosage) => (
                <option key={dosage} value={dosage} style={{ color: "black" }}>
                  {" "}
                  {/* Added style */}
                  {dosage}
                </option>
              ))}
            </Select>
          </Box>
          {/* Frequency */}
          <Box mb={4}>
            {" "}
            {/* Added mb={4} */}
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              mb="12px"
              color={colors.dark}
            >
              Frequency
            </Text>
            <Select
              placeholder={
                loadingFrequencies ? "Loading..." : "Select frequency"
              }
              focusBorderColor={colors.primary}
              value={formState.medicationHistory.frequency} // Bind value
              isDisabled={
                !formState.medicationHistory.dosage || loadingFrequencies
              } // Use formState value for disabled check
              onChange={(e) => handleInputChange("frequency", e.target.value)}
            >
              {frequencies?.map((frequency, index) => (
                <option
                  key={`${frequency}-${index}`}
                  value={frequency}
                  style={{ color: "black" }}
                >
                  {" "}
                  {/* Added style */}
                  {frequency}
                </option>
              ))}
            </Select>
          </Box>
          {/* Note */}
          <Box mb={4}>
            {" "}
            {/* Added mb={4} */}
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              mb="12px"
              color={colors.dark}
            >
              Notes
            </Text>
            <Textarea
              placeholder="Enter note" // Added placeholder
              focusBorderColor={colors.primary}
              value={formState.medicationHistory.note} // Bind value
              onChange={(e) => handleInputChange("note", e.target.value)}
            />
          </Box>

          <Flex style={{ justifyContent: "flex-end" }}>
            <Button
              theme="primary"
              text="Submit"
              className="rounded-md mt-2 w-[150px]"
              onClick={handleMedicationHistorySave}
              // Keep the original validation logic for disabling the button
              disabled={Boolean(
                isMedicationHistoryPending || isMedicationHistoryInvalid,
              )}
              loading={isMedicationHistoryPending} // Added loading state
            />
          </Flex>
        </>
      ) : type === "Past Medical History" ? (
        <>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Category
            </Text>
            <Select
              placeholder="Select category"
              focusBorderColor={colors.primary}
              value={formState.pastMedicalHistory.category}
              onChange={(e) => handleInputChange("category", e.target.value)}
            >
              <option value="Chronic Medical Conditions">
                Chronic Medical Conditions
              </option>
              <option value="Previous Surgeries">Previous Surgeries</option>
              <option value="Hospitalizations">Hospitalizations</option>
              <option
                value="Allergies
"
              >
                Allergies
              </option>
              <option value="Others">Others</option>
            </Select>
          </Box>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Notes
            </Text>
            <Textarea
              placeholder="Enter note"
              focusBorderColor={colors.primary}
              value={formState.pastMedicalHistory.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
            />
          </Box>
          <Flex style={{ justifyContent: "flex-end" }}>
            <Button
              theme="primary"
              text="Submit"
              className="rounded-md mt-2 w-[150px]"
              onClick={handleSave}
              disabled={Boolean(isPastMedicalHistoryPending || pmhAllEmpty)}
            />
          </Flex>
        </>
      ) : type === "Family History" ? (
        <>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Category
            </Text>
            <Select
              placeholder="Select category"
              focusBorderColor={colors.primary}
              value={formState.familyHistory.category}
              onChange={(e) => handleInputChange("category", e.target.value)}
            >
              <option value="Medical Conditions">Medical Conditions</option>
              <option value="Allergies">Allergies</option>
              <option value="Others">Others</option>
            </Select>
          </Box>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Family Member Affected
            </Text>
            <Select
              placeholder="Select family member"
              focusBorderColor={colors.primary}
              value={formState.familyHistory.familyMemberAffected}
              onChange={(e) =>
                handleInputChange("familyMemberAffected", e.target.value)
              }
            >
              <option value="Mother">Mother</option>
              <option value="Father">Father</option>
              <option value="Sibling">Sibling(s)</option>
              <option value="Grandparent">Extended relatives</option>
            </Select>
          </Box>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Notes
            </Text>
            <Textarea
              placeholder="Enter note"
              focusBorderColor={colors.primary}
              value={formState.familyHistory.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
            />
          </Box>
          <Flex style={{ justifyContent: "flex-end" }}>
            <Button
              theme="primary"
              text="Submit"
              className="rounded-md mt-2 w-[150px]"
              onClick={handleFamilyHistorySave}
              disabled={Boolean(
                isFamilyHistoryPending || isFamilyHistoryInvalid,
              )}
            />
          </Flex>
        </>
      ) : type === "Physical Findings" ? (
        <>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Title (Optional)
            </Text>
            <Input
              placeholder="Enter title (e.g., Cardiovascular Exam)"
              focusBorderColor={colors.primary}
              value={formState.physicalFindings.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
            />
          </Box>
          <Box mb={4}>
            <Text
              fontFamily="lato"
              fontSize="14px"
              fontWeight={600}
              lineHeight="17px"
              color={colors.dark}
              mb="12px"
            >
              Notes
            </Text>
            <Textarea
              placeholder="Enter physical findings note"
              focusBorderColor={colors.primary}
              value={formState.physicalFindings.note}
              onChange={(e) => handleInputChange("note", e.target.value)}
            />
          </Box>
          <Flex style={{ justifyContent: "flex-end" }}>
            <Button
              theme="primary"
              text="Submit"
              className="rounded-md mt-2 w-[150px]"
              onClick={handlePhyscicalFindingsSave}
              disabled={Boolean(isSavingNote || pfAllEmpty)}
            />
          </Flex>
        </>
      ) : (
        <Text
          fontFamily={"lato"}
          fontSize={"16px"}
          fontWeight={700}
          color={colors.dark}
        >
          {type}
        </Text>
      )}
    </Flex>
  );
};
export default ViewHistoryCard;
